/**
 * Selection Utility Functions
 * 
 * Centralized collection of selection-related helper functions for:
 * - Calculating bounds for selected strokes
 * - Intersection detection between strokes and selection rectangles
 * - Point transformations (move, resize, rotate)
 * - Resize/rotation handle detection
 * - Cursor management for selection tool
 */

/**
 * Calculate bounding box for a set of strokes
 * @param {Set} strokeIds - Set of stroke IDs to calculate bounds for
 * @param {Array} strokes - Array of all strokes
 * @returns {Object|null} Bounding box {x, y, width, height} or null if no valid bounds
 */
export const calculateStrokesBounds = (strokeIds, strokes) => {
    if (strokeIds.size === 0) return null;
    
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    strokes.forEach(stroke => {
        if (strokeIds.has(stroke.id) && stroke.points && stroke.points.length > 0) {
            if (stroke.type === 'text' && stroke.text && stroke.style) {
                // For text strokes, calculate bounds based on text dimensions
                const point = stroke.points[0];
                const fontSize = stroke.style.thickness ? stroke.style.thickness * 8 : 16; // Same calculation as in drawStroke
                const textWidth = stroke.text.length * fontSize * 0.6; // Approximate text width
                const textHeight = fontSize;
                
                minX = Math.min(minX, point.x);
                minY = Math.min(minY, point.y);
                maxX = Math.max(maxX, point.x + textWidth);
                maxY = Math.max(maxY, point.y + textHeight);
            } else {
                // For regular strokes, use all points
                stroke.points.forEach(point => {
                    minX = Math.min(minX, point.x);
                    minY = Math.min(minY, point.y);
                    maxX = Math.max(maxX, point.x);
                    maxY = Math.max(maxY, point.y);
                });
            }
        }
    });
    
    if (!isFinite(minX) || !isFinite(minY) || !isFinite(maxX) || !isFinite(maxY)) {
        return null;
    }
    
    return {
        x: minX,
        y: minY,
        width: maxX - minX,
        height: maxY - minY
    };
};

/**
 * Check if a stroke intersects with a selection rectangle
 * @param {Object} stroke - Stroke to check
 * @param {Object} rect - Selection rectangle {x, y, width, height}
 * @returns {boolean} True if stroke intersects with rectangle
 */
export const strokeIntersectsRect = (stroke, rect) => {
    if (!stroke || !stroke.points || stroke.points.length === 0) return false;
    
    if (stroke.type === 'text' && stroke.text) {
        // For text strokes, check if the text bounds intersect with selection rect
        const point = stroke.points[0];
        const fontSize = stroke.style?.thickness ? stroke.style.thickness * 8 : 16;
        const textWidth = stroke.text.length * fontSize * 0.6;
        const textHeight = fontSize;
        
        // Check if text bounds intersect with selection rectangle
        return !(point.x + textWidth < rect.x || 
                point.x > rect.x + rect.width ||
                point.y + textHeight < rect.y || 
                point.y > rect.y + rect.height);
    } else {
        // For regular strokes, check if any point is within the rectangle
        return stroke.points.some(point => 
            point.x >= rect.x && 
            point.x <= rect.x + rect.width &&
            point.y >= rect.y && 
            point.y <= rect.y + rect.height
        );
    }
};

/**
 * Find strokes within selection rectangle
 * @param {Object} selectionRect - Selection rectangle {x, y, width, height}
 * @param {Array} strokes - Array of all strokes
 * @returns {Set} Set of stroke IDs that intersect with selection
 */
export const getStrokesInSelection = (selectionRect, strokes) => {
    const selectedIds = new Set();
    strokes.forEach(stroke => {
        if (stroke && strokeIntersectsRect(stroke, selectionRect)) {
            selectedIds.add(stroke.id);
        }
    });
    return selectedIds;
};

/**
 * Transform stroke points (for move, resize, and rotate operations)
 * @param {Array} points - Array of points to transform
 * @param {Object} transform - Transform parameters {offsetX, offsetY, scaleX, scaleY, originX, originY, rotation}
 * @returns {Array} Transformed points
 */
export const transformStrokePoints = (points, transform) => {
    // Safety check: ensure points is an array
    if (!points || !Array.isArray(points)) {
        return [];
    }
    
    const { offsetX = 0, offsetY = 0, scaleX = 1, scaleY = 1, originX = 0, originY = 0, rotation = 0 } = transform;
    
    return points.map(point => {
        let x = point.x;
        let y = point.y;
        
        // Apply rotation first (if specified)
        if (rotation !== 0) {
            const cos = Math.cos(rotation);
            const sin = Math.sin(rotation);
            const dx = x - originX;
            const dy = y - originY;
            x = originX + (dx * cos - dy * sin);
            y = originY + (dx * sin + dy * cos);
        }
        
        // Then apply scaling
        x = originX + (x - originX) * scaleX;
        y = originY + (y - originY) * scaleY;
        
        // Finally apply translation
        x += offsetX;
        y += offsetY;
        
        return { ...point, x, y };
    });
};

/**
 * Get resize handle at point (returns handle type or null)
 * @param {Object} point - Point to check {x, y}
 * @param {Object} bounds - Selection bounds {x, y, width, height}
 * @returns {string|null} Handle type ('nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e') or null
 */
export const getResizeHandleAtPoint = (point, bounds) => {
    if (!bounds) return null;
    
    const handleSize = 12; // Increased from 8
    const tolerance = 16; // Much larger tolerance area for easier grabbing
    
    const handles = [
        { type: 'nw', x: bounds.x, y: bounds.y },
        { type: 'ne', x: bounds.x + bounds.width, y: bounds.y },
        { type: 'sw', x: bounds.x, y: bounds.y + bounds.height },
        { type: 'se', x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { type: 'n', x: bounds.x + bounds.width / 2, y: bounds.y },
        { type: 's', x: bounds.x + bounds.width / 2, y: bounds.y + bounds.height },
        { type: 'w', x: bounds.x, y: bounds.y + bounds.height / 2 },
        { type: 'e', x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 }
    ];
    
    for (const handle of handles) {
        const distance = Math.sqrt(
            Math.pow(point.x - handle.x, 2) + Math.pow(point.y - handle.y, 2)
        );
        if (distance <= tolerance) {
            return handle.type;
        }
    }
    
    return null;
};

/**
 * Get rotation handle at point (returns true if on rotation handle)
 * @param {Object} point - Point to check {x, y}
 * @param {Object} bounds - Selection bounds {x, y, width, height}
 * @returns {boolean} True if point is on rotation handle
 */
export const getRotationHandleAtPoint = (point, bounds) => {
    if (!bounds) return false;
    
    const tolerance = 16;
    const rotationHandleDistance = 40; // Distance above the selection bounds
    
    // Rotation handle position (centered above the selection)
    const handleX = bounds.x + bounds.width / 2;
    const handleY = bounds.y - rotationHandleDistance;
    
    const distance = Math.sqrt(
        Math.pow(point.x - handleX, 2) + Math.pow(point.y - handleY, 2)
    );
    
    return distance <= tolerance;
};

/**
 * Check if point is inside selection bounds (for dragging)
 * @param {Object} point - Point to check {x, y}
 * @param {Object} bounds - Selection bounds {x, y, width, height}
 * @returns {boolean} True if point is inside bounds
 */
export const isPointInSelectionBounds = (point, bounds) => {
    if (!bounds) return false;
    return point.x >= bounds.x && 
           point.x <= bounds.x + bounds.width &&
           point.y >= bounds.y && 
           point.y <= bounds.y + bounds.height;
};

/**
 * Get cursor style for resize handles
 * @param {string} handleType - Handle type ('nw', 'ne', 'sw', 'se', 'n', 's', 'w', 'e')
 * @returns {string} CSS cursor style
 */
export const getCursorForHandle = (handleType) => {
    switch (handleType) {
        case 'nw': case 'se': return 'nw-resize';
        case 'ne': case 'sw': return 'ne-resize';
        case 'n': case 's': return 'ns-resize';
        case 'w': case 'e': return 'ew-resize';
        default: return 'default';
    }
};

/**
 * Draw selection visual feedback on canvas
 * @param {CanvasRenderingContext2D} ctx - Canvas context
 * @param {boolean} isSelecting - Whether currently selecting
 * @param {Object} selectionBox - Current selection box {x, y, width, height}
 * @param {Set} selectedStrokes - Set of selected stroke IDs
 * @param {Object} selectionBounds - Bounds of selected strokes {x, y, width, height}
 */
export const drawSelectionVisuals = (ctx, isSelecting, selectionBox, selectedStrokes, selectionBounds) => {
    // Draw selection rectangle while selecting
    if (isSelecting && selectionBox) {
        ctx.save();
        ctx.strokeStyle = '#0066cc';
        ctx.fillStyle = 'rgba(0, 102, 204, 0.1)';
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);
        
        ctx.fillRect(selectionBox.x, selectionBox.y, selectionBox.width, selectionBox.height);
        ctx.strokeRect(selectionBox.x, selectionBox.y, selectionBox.width, selectionBox.height);
        ctx.restore();
    }
    
    // Draw selection bounds and handles for selected strokes
    if (selectedStrokes.size > 0 && selectionBounds) {
        ctx.save();
        
        // Draw selection bounds
        ctx.strokeStyle = '#0066cc';
        ctx.lineWidth = 2;
        ctx.setLineDash([8, 4]);
        ctx.strokeRect(selectionBounds.x, selectionBounds.y, selectionBounds.width, selectionBounds.height);
        
        // Draw resize handles
        const handleSize = 12; // Increased from 8 to match getResizeHandleAtPoint
        const handles = [
            { x: selectionBounds.x, y: selectionBounds.y }, // nw
            { x: selectionBounds.x + selectionBounds.width, y: selectionBounds.y }, // ne
            { x: selectionBounds.x, y: selectionBounds.y + selectionBounds.height }, // sw
            { x: selectionBounds.x + selectionBounds.width, y: selectionBounds.y + selectionBounds.height }, // se
            { x: selectionBounds.x + selectionBounds.width / 2, y: selectionBounds.y }, // n
            { x: selectionBounds.x + selectionBounds.width / 2, y: selectionBounds.y + selectionBounds.height }, // s
            { x: selectionBounds.x, y: selectionBounds.y + selectionBounds.height / 2 }, // w
            { x: selectionBounds.x + selectionBounds.width, y: selectionBounds.y + selectionBounds.height / 2 } // e
        ];
        
        ctx.fillStyle = '#0066cc';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2; // Thicker border for better visibility
        ctx.setLineDash([]);
        
        handles.forEach(handle => {
            ctx.fillRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
            ctx.strokeRect(handle.x - handleSize/2, handle.y - handleSize/2, handleSize, handleSize);
        });
        
        // Draw rotation handle
        const rotationHandleDistance = 40;
        const rotationHandleX = selectionBounds.x + selectionBounds.width / 2;
        const rotationHandleY = selectionBounds.y - rotationHandleDistance;
        const rotationHandleRadius = 8;
        
        // Draw connection line from top center to rotation handle
        ctx.strokeStyle = '#0066cc';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);
        ctx.beginPath();
        ctx.moveTo(selectionBounds.x + selectionBounds.width / 2, selectionBounds.y);
        ctx.lineTo(rotationHandleX, rotationHandleY);
        ctx.stroke();
        
        // Draw rotation handle as a circle
        ctx.fillStyle = '#0066cc';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.arc(rotationHandleX, rotationHandleY, rotationHandleRadius, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
        
        // Draw rotation icon (curved arrow)
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(rotationHandleX, rotationHandleY, rotationHandleRadius * 0.5, -Math.PI / 4, Math.PI * 1.5, false);
        ctx.stroke();
        // Arrow head
        ctx.beginPath();
        ctx.moveTo(rotationHandleX + rotationHandleRadius * 0.3, rotationHandleY - rotationHandleRadius * 0.3);
        ctx.lineTo(rotationHandleX + rotationHandleRadius * 0.5, rotationHandleY - rotationHandleRadius * 0.5);
        ctx.lineTo(rotationHandleX + rotationHandleRadius * 0.1, rotationHandleY - rotationHandleRadius * 0.5);
        ctx.stroke();
        
        ctx.restore();
    }
};
